import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { URL_EMPLOYEE } from '../../router/constants';
import useAnalytics from '../../hooks/useAnalytics';
import { PERMISSION_PERSON } from '../../Services/PermissionsService';
import { requirePermission } from '../../router/RouteGuardsService';
import Form from '../../components/Employee/Form';
import { EMPLOYEE_TABLE_NAME } from '../../components/Employee/constants';
import { ICmdTableFilter } from '../../components/CmdTable/types';
import {
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
} from '../../components/CmdTable/CmdTableState';
import { selectedCustomersAtom } from '../../store/jotai/customers';

export const Route = createFileRoute('/_authenticated/employee/add')({
  beforeLoad: async ({ context }) => {
    await requirePermission(PERMISSION_PERSON, ['create'], '/unauthorized', context);

    return {
      ...context,
      documentTitle: {
        main: 'employee.add',
        suffix: 'menu.employeeSettings',
      },
    };
  },
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: AddEmployeeRoute,
});

function AddEmployeeRoute() {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const selectedCustomers = useAtomValue(selectedCustomersAtom);
  const filterForRequest = useAtomValue<ICmdTableFilter>(
    filterForRequestAtomFamily(EMPLOYEE_TABLE_NAME)
  );
  const sortFilter = useAtomValue<{ [key: string]: string }>(
    sortFilterAtomFamily(EMPLOYEE_TABLE_NAME)
  );

  const handleClose = useCallback(() => {
    navigate({ to: URL_EMPLOYEE });
    trackEvent(['employees', 'click', 'Employees close']);
  }, [navigate, trackEvent]);

  const updateTable = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [EMPLOYEE_TABLE_NAME, filterForRequest, sortFilter, selectedCustomers],
    });
  }, [filterForRequest, queryClient, selectedCustomers, sortFilter]);

  return (
    <Form closeCallback={handleClose} editId={null} isEdited={false} updateTable={updateTable} />
  );
}
